import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import {
  User, Image, Loader2, Download, RefreshCw, Sparkles,
  Palette, Eye, Wand2
} from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import { toast } from "sonner";
import { API } from "../../lib/api";

const STYLES = [
  { id: "fantasy_portrait", label: "Fantasy", icon: "🏰" },
  { id: "realistic", label: "Realistic", icon: "📷" },
  { id: "anime", label: "Anime", icon: "🎨" },
  { id: "painted", label: "Oil Painting", icon: "🖼️" }
];

const CharacterPortraitModal = ({ 
  open, 
  onClose, 
  projectId,
  characters = []
}) => {
  const [selectedCharacter, setSelectedCharacter] = useState(null);
  const [style, setStyle] = useState("fantasy_portrait");
  const [mood, setMood] = useState("");
  const [customDetails, setCustomDetails] = useState("");
  
  const [generating, setGenerating] = useState(false);
  const [job, setJob] = useState(null);
  const [portraits, setPortraits] = useState({}); // {characterName: imageUrl}

  useEffect(() => {
    if (open && characters.length > 0 && !selectedCharacter) {
      setSelectedCharacter(characters[0]);
    }
  }, [open, characters]);

  useEffect(() => {
    if (!open) {
      setJob(null);
      setGenerating(false);
    }
  }, [open]);

  const handleGeneratePortrait = async () => {
    if (!selectedCharacter) {
      toast.error("Please select a character");
      return;
    }

    setGenerating(true);
    setJob(null);

    try {
      const res = await axios.post(`${API}/illustrations/character-portrait/jobs`, {
        project_id: projectId,
        character_name: selectedCharacter.name,
        character_description: selectedCharacter.description || selectedCharacter.role || "",
        physical_traits: selectedCharacter.physical_appearance || selectedCharacter.appearance || "",
        style,
        mood: mood || "neutral",
        custom_details: customDetails || ""
      });

      setJob({ id: res.data.job_id, status: "pending", progress: 0 });
      pollJob(res.data.job_id);
    } catch (error) {
      setGenerating(false);
      toast.error("Failed to start generation: " + (error.response?.data?.detail || error.message));
    }
  };

  const pollJob = async (jobId) => {
    try {
      const res = await axios.get(`${API}/illustrations/jobs/${jobId}`);
      setJob(res.data);

      if (res.data.status === "completed") {
        setGenerating(false);
        setPortraits(prev => ({
          ...prev,
          [res.data.character_name]: res.data.image_url
        }));
        toast.success("Portrait generated!");
      } else if (res.data.status === "failed") {
        setGenerating(false);
        toast.error("Generation failed: " + res.data.error);
      } else {
        setTimeout(() => pollJob(jobId), 2000);
      }
    } catch (error) {
      setGenerating(false);
      toast.error("Failed to check job status");
    }
  };

  const downloadImage = async (url, name) => {
    try {
      const response = await fetch(url);
      const blob = await response.blob();
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${name.replace(/\s+/g, '_')}_portrait.png`;
      link.click();
      toast.success("Image downloaded!");
    } catch (error) {
      toast.error("Download failed");
    }
  };

  const currentPortrait = selectedCharacter ? portraits[selectedCharacter.name] : null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-5xl bg-slate-900 border-slate-700 text-white">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <User className="w-5 h-5 text-pink-400" />
            Character Portraits
          </DialogTitle>
          <DialogDescription className="text-slate-400">
            Generate AI portraits for your characters
          </DialogDescription>
        </DialogHeader>

        <div className="grid md:grid-cols-2 gap-6 overflow-y-auto max-h-[65vh] pr-2">
          {/* Left: Controls */}
          <div className="space-y-4">
            {/* Character Selector */}
            <div>
              <label className="text-sm text-slate-400 block mb-2">Character:</label>
              <select
                value={selectedCharacter?.name || ""}
                onChange={(e) => {
                  const char = characters.find(c => c.name === e.target.value);
                  setSelectedCharacter(char);
                }}
                className="w-full bg-slate-800 border border-slate-600 rounded px-3 py-2 text-white"
              >
                {characters.length === 0 && (
                  <option value="">No characters defined</option>
                )}
                {characters.map(c => (
                  <option key={c.name} value={c.name}>
                    {c.name} {c.role ? `(${c.role})` : ""}
                  </option>
                ))}
              </select>
            </div>

            {/* Character Preview */}
            {selectedCharacter && (
              <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                <p className="text-sm font-medium text-white mb-1">{selectedCharacter.name}</p>
                {selectedCharacter.role && (
                  <Badge className="bg-cyan-500/20 text-cyan-400 text-xs mb-2">{selectedCharacter.role}</Badge>
                )}
                {(selectedCharacter.physical_appearance || selectedCharacter.appearance) && (
                  <p className="text-xs text-slate-400 line-clamp-3">
                    {selectedCharacter.physical_appearance || selectedCharacter.appearance}
                  </p>
                )}
                {selectedCharacter.description && (
                  <p className="text-xs text-slate-500 mt-1 line-clamp-2">
                    {selectedCharacter.description}
                  </p>
                )}
              </div>
            )}

            {/* Style Selector */}
            <div>
              <label className="text-sm text-slate-400 block mb-2">Style:</label>
              <div className="grid grid-cols-2 gap-2">
                {STYLES.map(s => (
                  <button
                    key={s.id}
                    onClick={() => setStyle(s.id)}
                    className={`px-3 py-2 rounded-lg border text-sm flex items-center gap-2 transition-all ${
                      style === s.id 
                        ? "bg-pink-600/20 border-pink-500/50 text-pink-300" 
                        : "bg-slate-800 border-slate-700 text-slate-400 hover:border-slate-600"
                    }`}
                  >
                    <span>{s.icon}</span>
                    <span>{s.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Custom Details */}
            <div>
              <label className="text-sm text-slate-400 block mb-1.5">Additional Details:</label>
              <textarea
                value={customDetails}
                onChange={(e) => setCustomDetails(e.target.value)}
                placeholder="Add details not in the profile — clothing, accessories, scars, tattoos, hair color, pose, setting, etc."
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-xs placeholder-slate-500 resize-none focus:border-pink-500/50 focus:ring-1 focus:ring-pink-500/20 outline-none"
                rows={2}
                data-testid="portrait-custom-details"
              />
            </div>

            {/* Mood Input */}
            <div>
              <label className="text-sm text-slate-400 block mb-1.5">Mood / Expression:</label>
              <input
                type="text"
                value={mood}
                onChange={(e) => setMood(e.target.value)}
                placeholder="e.g. glamour shot, sad, angry, mysterious, joyful, fierce, contemplative..."
                className="w-full bg-slate-800 border border-slate-700 rounded-lg px-3 py-2 text-white text-sm placeholder-slate-500 focus:border-violet-500/50 focus:ring-1 focus:ring-violet-500/20 outline-none"
                data-testid="portrait-mood-input"
              />
              <div className="flex flex-wrap gap-1.5 mt-2">
                {["glamour shot", "sad", "angry", "mysterious", "intense", "happy", "fierce", "contemplative"].map(m => (
                  <button
                    key={m}
                    onClick={() => setMood(m)}
                    className={`px-2 py-1 rounded border text-xs transition-all ${
                      mood === m 
                        ? "bg-violet-600/20 border-violet-500/50 text-violet-300" 
                        : "bg-slate-800/60 border-slate-700/50 text-slate-500 hover:text-slate-300 hover:border-slate-600"
                    }`}
                  >
                    {m}
                  </button>
                ))}
              </div>
            </div>

            {/* Generate Button */}
            <Button
              onClick={handleGeneratePortrait}
              disabled={generating || !selectedCharacter}
              className="w-full bg-pink-600 hover:bg-pink-500"
            >
              {generating ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Generating...</>
              ) : (
                <><Wand2 className="w-4 h-4 mr-2" /> Generate Portrait</>
              )}
            </Button>

            {/* Progress */}
            {job && job.status !== "completed" && job.status !== "failed" && (
              <div className="bg-slate-800/50 rounded-lg p-3 border border-slate-700/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-400">{job.status}</span>
                  <span className="text-xs text-pink-400">{job.progress}%</span>
                </div>
                <Progress value={job.progress} className="h-1.5 bg-slate-700" />
              </div>
            )}
          </div>

          {/* Right: Portrait Preview */}
          <div className="flex flex-col">
            <div className="flex-1 bg-slate-800/30 rounded-xl border border-slate-700/50 flex items-center justify-center min-h-[300px] overflow-hidden">
              {currentPortrait ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative w-full h-full"
                >
                  <img
                    src={currentPortrait}
                    alt={selectedCharacter?.name}
                    className="w-full h-full object-contain"
                  />
                </motion.div>
              ) : job?.status === "completed" && job?.image_url ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="relative w-full h-full"
                >
                  <img
                    src={job.image_url}
                    alt={selectedCharacter?.name}
                    className="w-full h-full object-contain"
                  />
                </motion.div>
              ) : (
                <div className="text-center text-slate-500 p-6">
                  <Image className="w-16 h-16 mb-3 opacity-30" />
                  <p className="text-sm">Portrait will appear here</p>
                  <p className="text-xs mt-1">Select a character and generate</p>
                </div>
              )}
            </div>

            {/* Actions */}
            {(currentPortrait || (job?.status === "completed" && job?.image_url)) && (
              <div className="flex gap-2 mt-3">
                <Button
                  onClick={() => downloadImage(currentPortrait || job.image_url, selectedCharacter?.name || "portrait")}
                  variant="outline"
                  className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
                <Button
                  onClick={handleGeneratePortrait}
                  disabled={generating}
                  variant="outline"
                  className="border-slate-600 text-slate-300 hover:bg-slate-800"
                >
                  <RefreshCw className="w-4 h-4" />
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Previously Generated */}
        {Object.keys(portraits).length > 1 && (
          <div className="mt-4 border-t border-slate-700 pt-4">
            <p className="text-sm text-slate-400 mb-2">Previously Generated:</p>
            <div className="flex gap-2 overflow-x-auto pb-2">
              {Object.entries(portraits).map(([name, url]) => (
                <button
                  key={name}
                  onClick={() => {
                    const char = characters.find(c => c.name === name);
                    if (char) setSelectedCharacter(char);
                  }}
                  className={`shrink-0 w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                    selectedCharacter?.name === name ? "border-pink-500" : "border-slate-700 hover:border-slate-600"
                  }`}
                >
                  <img src={url} alt={name} className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CharacterPortraitModal;
